/* eslint-disable */
import React, { useState } from "react";
import {
  Button,
  Group,
  Modal,
  TextInput,
  ScrollArea,
  List,
  Avatar,
  Loader,
  Center,
  Text,
  ActionIcon,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";

import ModalHeader from "components/ModalHeader";
import { useSearchOrgUsers } from "api/chat/userSearch";
import { useCreateConversation } from "api/chat/createConversation";
import { useGetConversations } from "api/chat/getConversation";
import { useQueryClient } from "@tanstack/react-query";

type SearchModalProps = {
  opened: boolean;
  onClose: () => void;
};

export default function SearchModal({ opened, onClose }: SearchModalProps) {
  const [q, setQ] = useState("");
  const [debouncedQ] = useDebouncedValue(q, 300);
  const queryClient = useQueryClient();

  const { mutate: createConversation } = useCreateConversation();

  const { data: users, isLoading, error } = useSearchOrgUsers(debouncedQ, 10);

  const clear = () => setQ("");

  const renderList = () => {
    if (isLoading) {
      return (
        <Center style={{ height: 300 }}>
          <Loader />
        </Center>
      );
    }

    if (error) {
      return (
        <Center style={{ height: 300 }}>
          <Text color="red">{error.message}</Text>
        </Center>
      );
    }

    if (!debouncedQ) {
      return (
        <Center style={{ height: 300 }}>
          <Text color="dimmed">Type to search users</Text>
        </Center>
      );
    }

    if (!users || users.length === 0) {
      return (
        <Center style={{ height: 300 }}>
          <Text color="dimmed">No users found</Text>
        </Center>
      );
    }

    return (
      <ScrollArea style={{ height: 300 }}>
        <List spacing="sm">
          {users.map((u) => {
            const initials = `${u.firstName?.[0] ?? ""}${
              u.lastName?.[0] ?? ""
            }`.toUpperCase();
            return (
              <List.Item key={u._id} py="xs">
                <Group noWrap align="center">
                  <Avatar radius="xl">{initials}</Avatar>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text lineClamp={1} weight={500}>
                      {u.firstName} {u.lastName}
                    </Text>
                    <Text size="sm" color="dimmed" lineClamp={1}>
                      {u.email}
                    </Text>
                  </div>

                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button size="xs" variant="light">
                      Select
                    </Button>
                  </div>
                </Group>
              </List.Item>
            );
          })}
        </List>
      </ScrollArea>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayOpacity={0.5}
      shadow="xs"
      withCloseButton={false}
      transitionDuration={300}
      centered
    >
      <ModalHeader
        title="search"
        onClose={onClose}
        ariaLabel="Close search modal"
      />

      <div style={{ marginTop: 8 }}>
        <Group align="center" spacing="sm" style={{ marginBottom: 12 }}>
          <TextInput
            placeholder="Search user..."
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1 }}
            rightSection={
              q ? (
                <ActionIcon onClick={clear} aria-label="clear search">
                  <IconX size={16} />
                </ActionIcon>
              ) : undefined
            }
          />
        </Group>

        {renderList()}
      </div>
    </Modal>
  );
}
